import React, { useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';

import CollapsingCheckBoxCollection from '@rover/components/src/formFields/CollapsingCheckBoxCollection';
import type { Checkbox } from '@rover/components/src/formFields/CollapsingCheckBoxCollection/CheckBoxCategory';
import {
  ADVANCED_FILTER_FIELDS,
  ARRAY_NUMBER_FIELDS,
  STAFF_ONLY_FIELDS,
} from '@rover/constants/src/search/searchPage.constants';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Heading } from '@rover/kibble/core';
import useRoverBrandContext from '@rover/react-lib/src/context/useRoverBrandContext';
import {
  SearchFilterLowBookingCheckboxClickEvent,
  SearchFilterLowBookingTooltipViewedEvent,
} from '@rover/react-lib/src/pages/search/SearchPage/analyticsEvents';
import StarSitterFilterLeftToggle from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/BooleanFilters/StarSitterFilterLeftToggle';
import PriceFilter from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/PriceFilter';
import { useSimplifiedDesktopFilters } from '@rover/react-lib/src/pages/search/SearchPage/utilities/useSimplifiedDesktopFilters';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import getIsCatOnlyPetType from '@rover/shared/js/search/utilities/getIsCatOnlyPetType';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import { PetType } from '@rover/types/src/Pet';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import {
  SearchDesktopToggleStarSitter,
  SearchDesktopUpdatePrice,
  SearchToggleBooleanFilter,
} from '../SimplifiedFiltersBar/analyticsEvents';

import CheckboxCollection from './common/CheckboxCollection';

const allAdvancedFilterNames = Array.from(ADVANCED_FILTER_FIELDS);
const staffOnlyFilterNames = Array.from(STAFF_ONLY_FIELDS).filter(
  (field) => !ARRAY_NUMBER_FIELDS.has(field)
);
const staffOnlyMultiValueFilterNames = Array.from(ARRAY_NUMBER_FIELDS);

const allFilters = allAdvancedFilterNames.concat(
  staffOnlyFilterNames,
  staffOnlyMultiValueFilterNames
);

const removeUnusedAndSortCheckboxes = (checkboxes: Array<Record<string, any>>): Checkbox[] => {
  if (!checkboxes) {
    return [];
  }

  return checkboxes
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank)
    .map(({ attribute, label, tooltip, value, onTooltipHover }) => ({
      attribute,
      label,
      tooltip,
      value,
      onTooltipHover,
    }));
};

type PriceFilterProps = {
  currencyCode: string;
  language: string;
  priceUnit: string;
  priceFilterMinValue: number;
  priceFilterMaxValue: number;
  isIncludingFees?: boolean;
  isStaffOrImpersonating?: boolean;
};

type Props = {
  advancedFiltersCount: number;

  onChange: (arg0: Record<string, any>) => void;
  searchFilters: Partial<SearchFilters>;
  searchFiltersOptions: SearchFiltersOptions;
  onCategoryExpandAndCollapse?: () => void;
  isPriceTransparency?: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  showLowBookingFilter?: boolean;
  onClose?: () => void;
  priceFilterProps?: PriceFilterProps;
  starSitterLink?: string;
  shouldShowPriceFilter?: boolean;
  shouldShowStarSitterFilter?: boolean;
  cardTreatment: SearchCardTreatment;
};

export const Advanced: React.FC<Props> = ({
  advancedFiltersCount,
  onChange,
  searchFilters,
  searchFiltersOptions,
  onCategoryExpandAndCollapse,
  isPriceTransparency,
  priceTransparencyLevel,
  showLowBookingFilter,
  onClose,
  priceFilterProps,
  starSitterLink,
  shouldShowPriceFilter = true,
  shouldShowStarSitterFilter = true,
  cardTreatment,
}) => {
  const { isActive: isSimplifiedFilters } = useSimplifiedDesktopFilters();
  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);
  const { isCatInAFlat } = useRoverBrandContext();

  const resetAll = (): void => {
    const resetFilters: Partial<SearchFilters> = allFilters.reduce(
      (acc, filterName) => ({
        ...acc,
        [filterName]: Array.isArray(searchFilters[filterName]) ? [] : false,
      }),
      {}
    );

    if (isSimplifiedFilters) {
      resetFilters.starSitter = false;
      if (priceFilterProps) {
        resetFilters.minprice = priceFilterProps.priceFilterMinValue;
        resetFilters.maxprice = priceFilterProps.priceFilterMaxValue;
      }
    }

    onChange(resetFilters);
  };

  const updateFilter = (
    updatedCheckboxFilter: Record<string, any>,
    checkboxValue?: any | null
  ): void => {
    const filterName = Object.keys(updatedCheckboxFilter)[0];
    const isChecked = updatedCheckboxFilter[filterName];
    const currentValue = searchFilters[filterName];

    if (filterName === 'hasLowBookingRate') {
      emitAnalyticsEvent(new SearchFilterLowBookingCheckboxClickEvent({ checked: isChecked }));
    }

    if (isSimplifiedFilters) {
      emitAnalyticsEvent(new SearchToggleBooleanFilter());
    }

    if (Array.isArray(currentValue)) {
      const newList = currentValue.includes(checkboxValue)
        ? currentValue
            .slice(0, currentValue.indexOf(checkboxValue))
            .concat(currentValue.slice(currentValue.indexOf(checkboxValue) + 1))
        : currentValue;

      if (isChecked) {
        onChange({ [filterName]: newList.concat([checkboxValue]) });
      } else {
        onChange({ [filterName]: newList });
      }
    } else {
      onChange({ [filterName]: isChecked });
    }
  };

  const isCatOnly = getIsCatOnlyPetType((searchFilters.petType as PetType[]) || []);
  const CAT_ONLY_FILTERS = ['catOralMedication', 'catInjectedMedication'];

  // Do not show puppy and cat care if price transparency is enabled
  // Only show cat-specific filters when all pet types are cats
  const advancedFilterNames = allAdvancedFilterNames
    .filter(
      (filterName) => !isPriceTransparency || (filterName !== 'puppy' && filterName !== 'catCare')
    )
    .filter((filterName) => (isCatInAFlat && isCatOnly) || !CAT_ONLY_FILTERS.includes(filterName));

  const categorizedCheckboxes = Object.keys(searchFiltersOptions).reduce(
    (accum: Record<string, any>, fieldName: string) => {
      if (advancedFilterNames.includes(fieldName) || staffOnlyFilterNames.includes(fieldName)) {
        const searchFilterOption = searchFiltersOptions[fieldName];
        const checkBox = {
          attribute: fieldName,
          ...searchFilterOption,
        };

        if (accum[searchFilterOption.category]) {
          accum[searchFilterOption.category].push(checkBox);
        } else {
          // eslint-disable-next-line no-param-reassign
          accum[searchFilterOption.category] = [checkBox];
        }
      }

      if (staffOnlyMultiValueFilterNames.includes(fieldName)) {
        const searchFilterOption = searchFiltersOptions[fieldName];
        // eslint-disable-next-line no-param-reassign
        accum[fieldName] = searchFilterOption.values.map(({ value, displayName }) => ({
          attribute: fieldName,
          tooltip: '',
          label: displayName,
          value,
        }));
      }

      return accum;
    },
    {}
  );

  // This filter was manually added to the advanced filters
  // because we have to dinamycally decided to show it or not base on
  // the user and the search place.
  if (showLowBookingFilter) {
    const lowBookingFilter = {
      attribute: 'hasLowBookingRate',
      category: 'advanced_services',
      categoryDisplayName: 'Services',
      categoryRank: 7,
      icon: 'filter-low_booking',
      label: t`Has low follow through rate`,
      rank: 10,
      required: false,
      tooltip: t`This option includes providers with very few repeat bookings or who don't often accept new clients after Meet & Greets.`,
      type: 'boolean',
      onTooltipHover: () => emitAnalyticsEvent(new SearchFilterLowBookingTooltipViewedEvent({})),
    };
    categorizedCheckboxes.advanced_services.push(lowBookingFilter);
  }

  const categories = [
    {
      category: t`Housing conditions`,
      rowsVisibleWhenCollapsed: 2,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.housing_conditions),
    },
    {
      category: t`Pets in the home`,
      rowsVisibleWhenCollapsed: 3,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.pets_in_the_home),
    },
    {
      category: t`Children in the home`,
      rowsVisibleWhenCollapsed: 1,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.children_in_the_home),
    },
    {
      category: t`Other`,
      rowsVisibleWhenCollapsed: 4,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.advanced_services),
    },
    {
      category: t`Sitter memberships`,
      rowsVisibleWhenCollapsed: 1,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.organizations),
    },
    {
      category: t`STAFF ONLY`,
      rowsVisibleWhenCollapsed: 4,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.staff_only),
    },
    {
      category: t`Agencies`,
      rowsVisibleWhenCollapsed: removeUnusedAndSortCheckboxes(categorizedCheckboxes.agency).length,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.agency),
    },
    {
      category: t`Provider Groups`,
      rowsVisibleWhenCollapsed: 4,
      checkboxes: removeUnusedAndSortCheckboxes(categorizedCheckboxes.providerGroup),
    },
  ].filter((category) => category.checkboxes.length);

  const showPriceFilter = isSimplifiedFilters && priceFilterProps && shouldShowPriceFilter;
  const showStarSitter = isSimplifiedFilters && shouldShowStarSitterFilter;

  const isChecked = useCallback(
    (filterName: string, value?: string | null): boolean => {
      return Array.isArray(searchFilters[filterName])
        ? searchFilters[filterName].includes(value)
        : !!searchFilters[filterName];
    },
    [searchFilters]
  );

  const booleanChecked = useMemo(() => {
    if (!isFiltersM2) return [];
    return categories.flatMap((category) =>
      category.checkboxes
        .filter((cb) => isChecked(cb.attribute, cb.value))
        .map((cb) => (cb.value != null ? String(cb.value) : cb.attribute))
    );
  }, [isFiltersM2, categories, searchFilters]);

  return (
    <>
      {isSimplifiedFilters && (
        <Flex
          background="primary"
          position="sticky"
          top="0"
          py="1x"
          justifyContent="space-between"
          alignItems="center"
          zIndex="3"
          display={['none', 'flex']}
        >
          {typeof advancedFiltersCount !== 'undefined' && (
            <Heading size="300" m="0x">
              <Trans>Filters</Trans> ({advancedFiltersCount})
            </Heading>
          )}
          <Flex alignItems="center">
            <Button
              variant="flat"
              size="small"
              onClick={resetAll}
              px="4x"
              aria-label={t`Reset all filters`}
            >
              <Trans>Reset all</Trans>
            </Button>
            <Button
              onClick={onClose}
              variant="flat"
              px="4x"
              pt="1x"
              pb="0x"
              aria-label={t`Close filters modal`}
            >
              <Close height="16" width="16" />
            </Button>
          </Flex>
        </Flex>
      )}
      {showPriceFilter && (
        <Box
          borderBottom="1px solid"
          borderColor="border.primary"
          pb="6x"
          pt="6x"
          display={['none', 'block']}
        >
          <PriceFilter
            currencyCode={priceFilterProps.currencyCode}
            language={priceFilterProps.language}
            priceUnit={priceFilterProps.priceUnit}
            priceTransparencyLevel={priceTransparencyLevel}
            isStaffOrImpersonating={priceFilterProps.isStaffOrImpersonating}
            onChange={({ min, max }) => {
              emitAnalyticsEvent(new SearchDesktopUpdatePrice({ cardTreatment }));
              onChange({ minprice: min, maxprice: max });
            }}
            selectedRangeMin={searchFilters.minprice ?? priceFilterProps.priceFilterMinValue}
            selectedRangeMax={searchFilters.maxprice ?? priceFilterProps.priceFilterMaxValue}
            sliderMax={priceFilterProps.priceFilterMaxValue}
            sliderMin={priceFilterProps.priceFilterMinValue}
            isIncludingFees={priceFilterProps.isIncludingFees}
          />
        </Box>
      )}
      {showStarSitter && (
        <Box
          borderBottom="1px solid"
          borderColor="border.primary"
          pb="6x"
          pt="6x"
          display={['none', 'block']}
        >
          <StarSitterFilterLeftToggle
            label={t`Only show Star Sitters`}
            onChange={(starSitter) => {
              if (isSimplifiedFilters) {
                emitAnalyticsEvent(new SearchDesktopToggleStarSitter({ enabled: starSitter }));
              }
              onChange({ starSitter });
            }}
            value={searchFilters.starSitter || false}
            starSitterLink={starSitterLink || ''}
          />
        </Box>
      )}
      {isFiltersM2 ? (
        <CheckboxCollection
          categories={categories}
          defaultChecked={booleanChecked}
          onChange={updateFilter}
        />
      ) : (
        <CollapsingCheckBoxCollection
          filtersCount={advancedFiltersCount}
          categories={categories}
          resetAllLinkText={t`Reset all`}
          resetAll={resetAll}
          onChange={updateFilter}
          isChecked={isChecked}
          onCategoryExpandAndCollapse={onCategoryExpandAndCollapse}
          showHeader={!isSimplifiedFilters || isMobileBrowser()}
        />
      )}
    </>
  );
};

export default Advanced;
